// Resolves how a user's plan is paid for, so the UI can show the right billing
// surface. Tool access gates on the tier string; this only governs the billing
// CTA. Modes:
//   free      — no plan (show Upgrade)
//   self      — paying via Lemon Squeezy (show Manage Billing / Manage Plan)
//   team_seat — seat funded by a team admin (show "Provided by your team")
//   granted   — complimentary admin grant, no LS subscription (show "Complimentary")
//
// Firestore rules permit a member to read their own team doc, so the team name
// is fetched directly for the team_seat case.

import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@shared/services/firebase';
import { useAuthStore } from '@shared/state';

export type BillingMode = 'free' | 'self' | 'team_seat' | 'granted';

interface BillingContext {
  mode: BillingMode;
  /** Team name when mode === 'team_seat'. */
  teamName: string | null;
}

export function useBillingContext(): BillingContext {
  const user = useAuthStore((s) => s.user);
  const u = user as
    | {
        subscription?: string;
        teamMemberOf?: string | null;
        subscriptionData?: { source?: string; lemonSqueezySubscriptionId?: string | null };
      }
    | null;

  const tier = u?.subscription ?? 'free';
  const source = u?.subscriptionData?.source;
  const hasLemonSub = !!u?.subscriptionData?.lemonSqueezySubscriptionId;
  const teamId = u?.teamMemberOf ?? null;

  let mode: BillingMode;
  if (tier === 'free') mode = 'free';
  else if (source === 'team_seat' && teamId) mode = 'team_seat';
  else if (!hasLemonSub) mode = 'granted'; // paid tier, no LS subscription → complimentary grant
  else mode = 'self';

  const [teamName, setTeamName] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (mode !== 'team_seat' || !teamId) {
      setTeamName(null);
      return;
    }
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'teams', teamId));
        if (!cancelled) setTeamName((snap.data()?.name as string) ?? null);
      } catch {
        if (!cancelled) setTeamName(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, teamId]);

  return { mode, teamName };
}
